/**
 * Upload a tracker workbook, and read what happened to it.
 *
 * The reconciliation is the feature, not the upload. Anyone can drag a
 * spreadsheet at a system; what nobody gets is a straight answer about which
 * rows landed, which did not, and which columns the system had no home for. So
 * the panel does not close on success — the report is the thing you came for,
 * and dismissing it to a toast would throw away the only record of what the
 * file actually contained.
 *
 * Sheets are matched by their header rows, so tabs can be called anything and
 * columns can be in any order. A tab that matches nothing is listed with its
 * reason rather than passed over in silence.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { canSeeAllCompanies, useAuth } from '../store/auth';
import { useImpersonation } from '../store/impersonation';
import { formatPhone } from '../lib/phone';
import { formatFilterDate } from '../lib/filterDate';
import { hasOneCompany } from '../lib/companyScope';
import type {
  ImportResult,
  SheetOutcome,
  SliceProgress,
  StagedUpload,
  UnfinishedImport,
} from '../types';

const XLSX_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Everything an import can touch. Narrower would leave a stale screen. */
const TOUCHED_BY_AN_IMPORT = [
  'candidates', 'calls', 'submissions', 'placements', 'tracker', 'clients',
  'my-day', 'followups',
];

/** Which screen each kind of imported row appears on. */
const WHERE_ROWS_LAND: Record<string, string> = {
  submissions: 'the Joining Tracker',
  calls: 'the Daily Calls Report',
  placements: 'the Placements screen',
};

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2">
      <div className={`text-lg font-semibold tabular-nums ${tone ?? 'text-gray-900'}`}>
        {value.toLocaleString('en-IN')}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}

function SheetReport({ sheet }: { sheet: SheetOutcome }) {
  const report = sheet.report;

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-medium text-gray-900">{sheet.sheetName}</h4>
        {sheet.detectedAs ? (
          <span className="badge bg-emerald-100 text-emerald-700">
            {sheet.detectedAs}
          </span>
        ) : (
          <span className="badge bg-gray-100 text-gray-600">not recognised</span>
        )}
      </div>

      {/* A tab that imported nothing says why, rather than being absent. */}
      {sheet.error && (
        <p className="mt-1.5 text-xs text-gray-600">{sheet.error}</p>
      )}

      {/* Where the rows went.
          The upload button sits on Interview Candidates, which is the one
          screen that structurally cannot show what a Daily Tracker import
          creates: it lists candidates whose most recent *call* was positive,
          and an import creates submissions and no calls at all. Somebody who
          imports 2,291 rows and stays on this page sees nothing and has no
          way to learn why. */}
      {report && sheet.kind && (report.created > 0 || report.updated > 0) && (
        <p className="mt-1.5 text-xs text-gray-600">
          These rows are on{' '}
          <span className="font-medium text-gray-900">
            {WHERE_ROWS_LAND[sheet.kind] ?? 'their own screen'}
          </span>
          {sheet.kind === 'submissions' && (
            <>
              {' '}— not on this list, which shows only candidates whose most
              recent call was Interested or Shortlisted. An import records
              submissions, not calls.
            </>
          )}
        </p>
      )}

      {report && (
        <p className="mt-1.5 text-xs text-gray-600">
          {report.rowsRead} read · {report.created} created · {report.updated} updated
          {report.skipped > 0 && ` · ${report.skipped} skipped`}
          {report.failed > 0 && ` · ${report.failed} failed`}
        </p>
      )}

      {/* Above the counters it explains, not below them: somebody who reads
          "0 created" and stops should already have been told why. */}
      {report?.nothingNew && (
        <p className="mt-1.5 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {report.nothingNew}
        </p>
      )}

      {/* The number somebody uploading a Joining Tracker is actually asking
          about. "10 created · 245 updated" says the file was read; this says
          the rows will be on the screen they were uploaded for. */}
      {report && report.submissionsLinked > 0 && (
        <p className="mt-0.5 text-xs text-emerald-800">
          {report.submissionsLinked} row
          {report.submissionsLinked === 1 ? '' : 's'} now on the Joining Tracker
        </p>
      )}

      {/* Not a skip — the placement is real and is on the Placements screen.
          It simply has no date to be filed under, and saying which fix is
          needed beats letting somebody hunt for rows that never appear. */}
      {report && report.undateable > 0 && (
        <p className="mt-1.5 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {report.undateable} row{report.undateable === 1 ? '' : 's'} stayed off
          the Joining Tracker: no joining date and no interview date, so there
          is no month to file them under. They are on the Placements screen.
          Add a DOJ in the sheet and upload again.
        </p>
      )}

      {/* Said next to the skip count, not above it: on a Daily Tracker sheet
          the same rows can be skipped submissions and created people. */}
      {report && report.candidatesCreated + report.candidatesUpdated > 0 && (
        <p className="mt-0.5 text-xs text-emerald-800">
          {report.candidatesCreated} candidate
          {report.candidatesCreated === 1 ? '' : 's'} created
          {report.candidatesUpdated > 0
            && `, ${report.candidatesUpdated} updated`} from these rows
        </p>
      )}

      {/* Counted since the importer was written and never shown. On a real
          workbook this was 1,012 of 2,291 rows — every one imported, every
          one with a date the importer had to choose the reading of. */}
      {report && report.ambiguousDates > 0 && (
        <p className="mt-1.5 text-xs text-amber-800">
          {report.ambiguousDates} date
          {report.ambiguousDates === 1 ? '' : 's'} could be read two ways —
          {' '}01.12.2025 is 1 December here, 12 January in an American sheet.
          Read day-first. The rows imported; check these if the months look
          wrong.
        </p>
      )}

      {/* Beside the ambiguous-dates line because it is the same kind of
          problem one step worse: there the importer chose between two
          readings of a date, here there was no date to read and it used the
          row above. Split by reason, because a phone number in the Date
          column has a right answer somewhere and an unreadable cell does
          not. */}
      {report && report.carriedDates > 0 && (
        <details className="mt-1.5 rounded border border-amber-200 bg-amber-50 p-2">
          <summary className="cursor-pointer text-xs font-medium text-amber-900">
            {report.carriedDates} row{report.carriedDates === 1 ? '' : 's'}
            {' '}took the date of the row above
          </summary>
          <p className="mt-1 text-[11px] text-amber-800">
            These rows imported — the calls happened — but their own Date cell
            held something that is not a date, so each was filed under the last
            date the sheet gave. Check them if a day looks short.
          </p>
          <ul className="mt-1 space-y-0.5 text-[11px] text-amber-900">
            {report.carriedDateReasons.map((r) => (
              <li key={r.reason}>
                {r.count} × {r.reason}
              </li>
            ))}
          </ul>
          <table className="mt-1.5 w-full text-left text-[11px] text-amber-900">
            <thead className="text-amber-700">
              <tr>
                <th className="pr-2 font-medium">Row</th>
                <th className="pr-2 font-medium">Cell held</th>
                <th className="font-medium">Filed under</th>
              </tr>
            </thead>
            <tbody>
              {report.carriedDateRows.map((c) => (
                <tr key={c.row}>
                  <td className="pr-2 tabular-nums">{c.row}</td>
                  <td className="pr-2 tabular-nums">{c.raw ?? '—'}</td>
                  <td className="tabular-nums">{formatFilterDate(c.used)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {report.carriedDateRowsTruncated > 0 && (
            <p className="mt-1 text-[11px] text-amber-700">
              {report.carriedDateRowsTruncated} further rows not listed.
            </p>
          )}
        </details>
      )}

      {report && report.skipReasons.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-gray-500">
          {report.skipReasons.map((s) => (
            <li key={s.reason}>
              {s.count} × {s.reason}
            </li>
          ))}
        </ul>
      )}

      {/* The counts above say how many and what kind. This says which — with
          the number on each row, because "3 skipped" that cannot be checked
          is indistinguishable from 3 rows quietly lost. */}
      {report && report.skippedRows?.length > 0 && (
        <details className="mt-2 rounded border border-amber-200 bg-amber-50 p-2">
          <summary className="cursor-pointer text-xs font-medium text-amber-900">
            Which rows were skipped ({report.skipped})
          </summary>
          <table className="mt-1.5 w-full text-left text-[11px] text-amber-900">
            <thead className="text-amber-700">
              <tr>
                <th className="pr-2 font-medium">Row</th>
                <th className="pr-2 font-medium">Mobile</th>
                <th className="pr-2 font-medium">Name</th>
                <th className="font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {report.skippedRows.map((s) => (
                <tr key={`${s.row}-${s.reason}`}>
                  <td className="pr-2 tabular-nums">{s.row}</td>
                  <td className="pr-2 tabular-nums">{formatPhone(s.phone) ?? '—'}</td>
                  <td className="pr-2">{s.name ?? '—'}</td>
                  <td>{s.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {report.skippedRowsTruncated > 0 && (
            <p className="mt-1 text-[11px] text-amber-700">
              {report.skippedRowsTruncated} further skipped rows not listed.
            </p>
          )}
        </details>
      )}

      {/* Imported, but outside the duplicate check. Said plainly because the
          consequence lands later: re-uploading this file doubles these rows
          and nothing will stop it. */}
      {report && report.undedupableRows?.length > 0 && (
        <details className="mt-2 rounded border border-gray-200 bg-gray-50 p-2">
          <summary className="cursor-pointer text-xs font-medium text-gray-700">
            {report.undedupableRows.length} imported without a usable mobile
          </summary>
          <p className="mt-1 text-[11px] text-gray-600">
            These rows were kept — the call happened — but with no readable
            number they could not be checked against existing calls. Uploading
            this file again would duplicate them.
          </p>
          <ul className="mt-1 space-y-0.5 text-[11px] text-gray-700">
            {report.undedupableRows.map((u) => (
              <li key={u.row}>
                Row {u.row}
                {u.name ? ` · ${u.name}` : ''} · {u.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      {sheet.unrecognisedHeaders.length > 0 && (
        <div className="mt-2.5">
          <p className="text-xs font-medium text-amber-800">
            Columns with no field yet
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            {sheet.unrecognisedHeaders.map((header) => (
              <span
                key={header}
                className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800 ring-1 ring-amber-200"
              >
                {header}
              </span>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">
            Their values are kept in the import record below, not on the
            candidate. Nothing was discarded.
          </p>
        </div>
      )}

      {sheet.unmappedRows.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-gray-600">
            What those columns contained ({sheet.unmappedRows.length} rows
            {sheet.unmappedTruncated > 0
              ? `, ${sheet.unmappedTruncated} more not listed`
              : ''}
            )
          </summary>
          <div className="mt-1.5 max-h-52 overflow-y-auto rounded border border-gray-200">
            <table className="w-full text-xs">
              <tbody className="divide-y divide-gray-100">
                {sheet.unmappedRows.map((row) =>
                  row.cells.map((cell) => (
                    <tr key={`${row.row}-${cell.header}`}>
                      <td className="w-12 px-2 py-1 text-right tabular-nums text-gray-400">
                        {row.row}
                      </td>
                      <td className="px-2 py-1 text-gray-500">{cell.header}</td>
                      <td className="px-2 py-1 text-gray-900">{cell.value}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {sheet.missingFields.length > 0 && (
        <p className="mt-2 text-[11px] text-gray-400">
          Not in this sheet: {sheet.missingFields.join(', ')}
        </p>
      )}

      {report && report.errors.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-red-700">
            {report.errors.length} row{report.errors.length === 1 ? '' : 's'} failed
          </summary>
          <ul className="mt-1 space-y-0.5 text-[11px] text-red-700">
            {report.errors.map((e) => (
              <li key={e.row}>Row {e.row}: {e.error}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * `endpoint` is what separates the two callers.
 *
 * The Candidates page posts to the full importer, which reads every known
 * sheet and is company-admin only. The Daily Calls Report posts to the
 * calls-only route, which any staff member may use and which refuses a
 * placements or submissions tab by name. The panel itself is identical: the
 * reconciliation is the feature either way, and having two copies of it would
 * mean one of them quietly falling behind.
 */
export default function ImportPanel({
  onClose,
  endpoint = '/api/v1/imports',
  stageEndpoint,
  title = 'Import a tracker workbook',
}: {
  onClose: () => void;
  endpoint?: string;
  /**
   * Set this and the upload runs in slices instead of one request.
   *
   * The file is parsed once and its rows parked server-side; the browser then
   * asks for a few thousand at a time until they are all in. Same importer,
   * same rows, same reconciliation — the difference is that no single request
   * has to survive eight and a half minutes, which is what a 62,728-row
   * workbook was asking for and not getting.
   *
   * Only the calls route offers this. The full importer creates users and
   * pipeline from a workbook read whole, and slicing that is a different
   * problem from slicing a list of calls.
   */
  stageEndpoint?: string;
  title?: string;
}) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  /**
   * Whether a Client cell naming nobody on file may become a client.
   *
   * On — every import before this existed — an unmatched spelling creates a
   * company so the row can be written, which is how one hospital became eight
   * client rows: Apollo, Apollo Homecare, Apollo Homecare Nurse, Apollo
   * Pharmacist and so on, each a designation glued to a company name and each
   * now a separate line in every per-client report.
   *
   * Off, those rows are skipped and every rejected spelling comes back in the
   * report. Default on, because a first import into an empty company has
   * nothing for any name to match and off would skip the whole file.
   */
  const [createClients, setCreateClients] = useState(true);
  const [result, setResult] = useState<ImportResult | null>(null);

  /** The staged upload and where each of its sheets has got to. */
  const [staged, setStaged] = useState<StagedUpload | null>(null);
  const [progress, setProgress] = useState<Record<string, SliceProgress>>({});

  /**
   * Set when the panel closes mid-import, and checked between slices.
   *
   * A ref rather than state because the loop reads it after every await, and
   * a state value captured when the loop started would never change. The rows
   * already imported stay imported — they committed — but nothing further is
   * asked for, and the staged rows are deleted on the way out.
   */
  const abandoned = useRef(false);

  /**
   * An owner who has not chosen a company cannot import into one.
   *
   * Both routes resolve the tenant through resolve_company_id, which has no
   * company to infer for a super admin and answers 400 "Super admin must
   * specify a company_id for this operation" — our internals, arriving after
   * the file has been picked and uploaded. The top bar already says to choose
   * one; this stops the upload being offered anyway, the same way the custom
   * fields form does.
   *
   * Not while viewing as somebody else: the request goes out under their
   * token, so the company is theirs and the picker is hidden.
   */
  const user = useAuth((s) => s.user);
  const actingCompanyId = useAuth((s) => s.actingCompanyId);
  const viewing = useImpersonation((s) => s.target);
  // hasOneCompany, not "something is chosen": rows have to belong to a
  // company, and "All companies" is not one. The server says the same with a
  // 400, but after the file has already been uploaded.
  const needsCompany =
    canSeeAllCompanies(user) && !viewing && !hasOneCompany(actingCompanyId);

  const refreshEverything = () => {
    for (const key of TOUCHED_BY_AN_IMPORT) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  /**
   * Imports this person started and did not finish.
   *
   * Asked for as soon as the panel opens, because the moment somebody wants
   * this is the moment they come back to import the same file again. Without
   * it the only remedy is to upload the whole workbook and let the duplicate
   * check absorb the half that already landed — correct, and another eight
   * minutes.
   */
  const unfinished = useQuery({
    queryKey: ['imports', 'unfinished'],
    queryFn: async () =>
      (await client.get('/api/v1/imports/batches/unfinished'))
        .data as UnfinishedImport[],
    enabled: Boolean(stageEndpoint) && !needsCompany,
  });

  /** Ask for slices of one batch until it says done. Shared by both paths. */
  const drain = async (batchId: string) => {
    for (;;) {
      if (abandoned.current) return;
      const next = (
        await client.post(`/api/v1/imports/batches/${batchId}/slice`)
      ).data as SliceProgress;
      setProgress((current) => ({ ...current, [batchId]: next }));
      // Refresh as we go. Half an import is a real state of the database, and
      // a Daily Calls Report still showing yesterday's count while rows land
      // behind it is how somebody concludes nothing is happening.
      refreshEverything();
      if (next.done) return;
    }
  };

  const resume = useMutation({
    mutationFn: async (batch: UnfinishedImport) => {
      abandoned.current = false;
      setStaged({
        filename: batch.filename,
        sheets: [],
        refused: [],
        workbookWarnings: 0,
        workbookWarningSample: [],
      });
      setProgress({
        [batch.batchId]: {
          batchId: batch.batchId,
          sheetName: batch.sheetName,
          kind: batch.kind,
          status: 'running',
          rowsTotal: batch.rowsTotal,
          rowsProcessed: batch.rowsProcessed,
          done: false,
          created: batch.created,
          updated: batch.updated,
          skipped: batch.skipped,
          failed: batch.failed,
          slice: null,
          note: null,
        },
      });
      await drain(batch.batchId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['imports', 'unfinished'] });
      refreshEverything();
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not continue that import')),
  });

  const discard = useMutation({
    mutationFn: async (batchId: string) => {
      await client.delete(`/api/v1/imports/batches/${batchId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['imports', 'unfinished'] }),
  });

  /**
   * Stage the file, then ask for slices until every sheet says done.
   *
   * Sequential on purpose. The slices of one sheet have to run in order —
   * the importer carries the last seen date forward — and running two sheets
   * at once would have them competing for the same database connection to
   * finish no sooner.
   */
  const runInSlices = async (chosen: File) => {
    abandoned.current = false;
    setProgress({});

    const form = new FormData();
    form.append('file', chosen);
    const upload = (await client.post(stageEndpoint!, form)).data as StagedUpload;
    setStaged(upload);

    for (const sheet of upload.sheets) {
      // Seed the bar at zero so a large sheet shows something immediately,
      // rather than nothing until its first slice comes back.
      setProgress((current) => ({
        ...current,
        [sheet.batchId]: {
          batchId: sheet.batchId,
          sheetName: sheet.sheetName,
          kind: sheet.kind,
          status: 'staged',
          rowsTotal: sheet.rowsTotal,
          rowsProcessed: 0,
          done: false,
          created: 0,
          updated: 0,
          skipped: 0,
          failed: 0,
          slice: null,
          note: null,
        },
      }));

      await drain(sheet.batchId);
      if (abandoned.current) return upload;
    }

    return upload;
  };

  const upload = useMutation({
    mutationFn: async (chosen: File) => {
      if (stageEndpoint) return runInSlices(chosen);
      const form = new FormData();
      form.append('file', chosen);
      // No Content-Type override here: the request interceptor clears the
      // instance default for every FormData body, which is where that belongs.
      // Doing it per-caller is what left Documents broken.
      return (
        await client.post(endpoint, form, {
          params: { createMissingClients: createClients },
        })
      ).data as ImportResult;
    },
    onSuccess: (data) => {
      if (stageEndpoint) {
        // The sliced path reports through `progress`; there is no
        // ImportResult to show and the totals are already on screen.
        refreshEverything();
        return;
      }
      setResult(data as ImportResult);
      refreshEverything();
      // Green only when something actually entered the system. A file that
      // created nothing is not a failure — a re-import is an ordinary thing
      // to do — but it is not a success either, and a green tick is how
      // somebody closes the panel believing 2,291 rows landed.
      const done = data as ImportResult;
      const madeSomething = done.created > 0 || done.candidatesCreated > 0;
      const summary =
        `${done.candidatesCreated} candidates created, ${done.created} records `
        + `created from ${done.filename}`;

      if (madeSomething) {
        toast.success(summary);
      } else {
        toast.warning(`Nothing new from ${done.filename}`, {
          description:
            done.updated > 0
              ? 'Every row already existed — this file has been imported before.'
              : 'No rows were created. Open the reconciliation to see why.',
        });
      }
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not import that file')),
  });

  return (
    <div className="fixed inset-0 z-20 flex items-start justify-center overflow-y-auto bg-black/30 p-4">
      <div className="card my-8 w-full max-w-3xl space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Tabs are matched by their header row, so they can be named
              anything and the columns can be in any order.
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              // Stop asking for slices, and let the server drop the rows it
              // is holding. What already imported stays imported: it
              // committed, and nothing here can tell those rows apart from
              // ones that were already on record.
              // Stop asking for slices, and leave the rest where it is.
              //
              // Deliberately not deleted. An import stopped halfway is
              // resumable — the staged rows are on the server and the panel
              // offers them back next time it opens — and closing a dialog is
              // not the same as saying "throw the rest away". The Discard
              // button is how somebody says that, and the nightly sweep takes
              // anything nobody came back to within a day.
              abandoned.current = true;
              void queryClient.invalidateQueries({
                queryKey: ['imports', 'unfinished'],
              });
              onClose();
            }}
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            id="tracker-workbook-upload"
            type="file"
            accept=".xlsx"
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setResult(null);
            }}
          />
          <label
            htmlFor="tracker-workbook-upload"
            className={`inline-flex min-w-0 max-w-full cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${
              file
                ? 'border-indigo-200 bg-indigo-50 text-indigo-800 hover:border-indigo-300'
                : 'border-dashed border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400 hover:bg-amber-100'
            }`}
          >
            <FileSpreadsheet className="h-4 w-4 shrink-0" />
            <span className="font-medium">Choose Excel file</span>
            <span className="truncate text-gray-600">
              {file?.name ?? 'No file chosen'}
            </span>
          </label>
          <button
            type="button"
            className="btn-primary"
            disabled={!file || needsCompany || upload.isPending}
            onClick={() => file && upload.mutate(file)}
          >
            {upload.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Import
          </button>
        </div>

        {/* Only on the one-shot route. The sliced upload stages the file first
            and the flag would have to survive to every slice, which is a
            server-side change this control must not pretend to have made. */}
        {!stageEndpoint && (
          <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 accent-indigo-600"
              checked={!createClients}
              onChange={(e) => setCreateClients(!e.target.checked)}
            />
            <span>
              <span className="font-medium text-gray-800">
                Only use clients already on file
              </span>
              {' — '}rows naming a company this system does not know are
              skipped and listed, instead of creating it. Stops one client
              arriving as several spellings. Leave this off for the first
              import into a new company, or every row will be skipped.
            </span>
          </label>
        )}

        {needsCompany && (
          <p className="flex items-start gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Choose a company in the top bar first. Imported rows have to belong
            to one, and there is no way to guess which you meant.
          </p>
        )}

        {/* The server refuses anything else, and says so — but a file picker
            that accepts a .xls and then fails is a worse way to learn it. */}
        {file && file.type && file.type !== XLSX_TYPE && (
          <p className="flex items-start gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            That does not look like an .xlsx workbook. Only .xlsx can be
            imported.
          </p>
        )}

        {/* Offered before the file picker, because somebody who left an
            import half done is here to finish it, not to start another. */}
        {stageEndpoint && !staged && (unfinished.data?.length ?? 0) > 0 && (
          <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
            <h4 className="text-sm font-medium text-indigo-900">
              Unfinished import{unfinished.data!.length === 1 ? '' : 's'}
            </h4>
            <p className="text-xs text-indigo-800">
              These stopped partway — a closed tab, or a laptop that went to
              sleep. The rows that went in are in. The rest are still here.
            </p>
            {unfinished.data!.map((b) => (
              <div
                key={b.batchId}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-indigo-200 bg-white px-2.5 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-gray-900">
                    {b.filename}
                  </p>
                  <p className="text-[11px] tabular-nums text-gray-500">
                    {b.rowsProcessed.toLocaleString('en-IN')} of{' '}
                    {b.rowsTotal.toLocaleString('en-IN')} rows ·{' '}
                    {b.created.toLocaleString('en-IN')} created
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    className="btn-primary px-2.5 py-1 text-xs"
                    disabled={resume.isPending || discard.isPending}
                    onClick={() => resume.mutate(b)}
                  >
                    {resume.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : null}
                    Continue
                  </button>
                  <button
                    type="button"
                    className="btn-ghost px-2.5 py-1 text-xs"
                    disabled={resume.isPending || discard.isPending}
                    onClick={() => discard.mutate(b.batchId)}
                    title="Discard the rows still waiting. What already imported stays."
                  >
                    Discard
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {staged && (
          <div className="space-y-3 border-t border-gray-200 pt-3">
            {Object.values(progress).map((p) => {
              const pct = p.rowsTotal
                ? Math.round((p.rowsProcessed / p.rowsTotal) * 100)
                : 0;
              return (
                <div key={p.batchId} className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="font-medium text-gray-900">
                      {p.sheetName}
                    </span>
                    <span className="tabular-nums text-gray-500">
                      {p.rowsProcessed.toLocaleString('en-IN')} of{' '}
                      {p.rowsTotal.toLocaleString('en-IN')} rows
                    </span>
                  </div>
                  {/* A bar, because eight minutes of a spinner is
                      indistinguishable from a hung request — which is how the
                      same file came to be uploaded three times in one day. */}
                  <div className="h-1.5 w-full overflow-hidden rounded bg-gray-100">
                    <div
                      className={`h-full transition-all duration-300 ${
                        p.done ? 'bg-emerald-500' : 'bg-indigo-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                    <span className="text-emerald-700">{p.created} created</span>
                    <span className="text-indigo-700">{p.updated} updated</span>
                    <span className="text-amber-700">{p.skipped} skipped</span>
                    {p.failed > 0 && (
                      <span className="text-red-700">{p.failed} failed</span>
                    )}
                    {p.done && <span className="text-emerald-700">done</span>}
                  </div>
                </div>
              );
            })}

            {/* Named while it is still running, not after. A tab that was
                passed over has to say so; silence reads as "there was nothing
                in it". */}
            {staged.refused.length > 0 && (
              <ul className="space-y-0.5 text-[11px] text-amber-800">
                {staged.refused.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}

            {staged.workbookWarnings > 0 && (
              <p className="text-[11px] text-gray-500">
                {staged.workbookWarnings} cell
                {staged.workbookWarnings === 1 ? '' : 's'} could not be read as
                the type they are formatted as — usually a column formatted as
                dates with something else typed into it. Those rows still
                import, dated from the row above them.
              </p>
            )}

            {(upload.isPending || resume.isPending) && (
              <p className="text-[11px] text-gray-500">
                Keep this open until it finishes. Closing now stops where it
                is — what has gone in stays in, and the rest is offered back
                the next time you open this panel.
              </p>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3 border-t border-gray-200 pt-3">
            {/* Candidates sit on their own row, ahead of the record counts.
                They are the reason for the upload, and a row that skipped its
                submission may still have created the person — reading one
                number for both is what made "10 skipped" sound like nothing
                happened. */}
            <div className="grid grid-cols-2 gap-2">
              <Stat
                label="Candidates created"
                value={result.candidatesCreated}
                tone="text-emerald-700"
              />
              <Stat
                label="Candidates updated"
                value={result.candidatesUpdated}
                tone="text-indigo-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <Stat label="Rows read" value={result.rowsRead} />
              <Stat label="Records created" value={result.created} tone="text-emerald-700" />
              <Stat label="Records updated" value={result.updated} tone="text-indigo-700" />
              <Stat label="Skipped" value={result.skipped} tone="text-amber-700" />
              <Stat label="Failed" value={result.failed} tone="text-red-700" />
            </div>

            <div className="space-y-2">
              {result.sheets.map((sheet) => (
                <SheetReport key={sheet.sheetName} sheet={sheet} />
              ))}
            </div>

            {((result.reattributed ?? 0) > 0
              || (result.candidatesReowned ?? 0) > 0) && (
              <div className="rounded-lg border border-gray-200 p-3">
                <h4 className="text-sm font-medium text-gray-900">
                  Attribution Repaired
                </h4>
                <p className="mt-0.5 text-xs text-gray-500">
                  {result.reattributed} row
                  {result.reattributed === 1 ? '' : 's'} and{' '}
                  {result.candidatesReowned} candidate
                  {result.candidatesReowned === 1 ? '' : 's'} already existed
                  under your name and now belong to the recruiter this sheet
                  names. Anything somebody had already corrected was left alone.
                </p>
              </div>
            )}

            {(result.createdRecruiters ?? []).length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <h4 className="text-sm font-medium text-gray-900">
                  New Recruiters From This Sheet
                </h4>
                <p className="mt-0.5 text-xs text-gray-500">
                  The Recruiter column named people who were not users here, so
                  each got a deactivated account and owns their own rows. They
                  cannot sign in. Check the spellings — a typo makes a person.
                </p>
                <ul className="mt-2 space-y-0.5">
                  {(result.createdRecruiters ?? []).map((r) => (
                    <li key={r.name} className="text-xs text-gray-900">
                      {r.name}
                      <span className="text-gray-500">
                        {' '}— {r.rows} row{r.rows === 1 ? '' : 's'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(result.unresolvedRecruiters ?? []).length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <h4 className="text-sm font-medium text-amber-900">
                  Filed Under You
                </h4>
                <p className="mt-0.5 text-xs text-amber-800">
                  These Recruiter names match no user here. Their rows are
                  attributed to you, because this upload does not create
                  accounts.
                </p>
                <ul className="mt-2 space-y-0.5">
                  {(result.unresolvedRecruiters ?? []).map((r) => (
                    <li key={r.name} className="text-xs text-amber-900">
                      {r.name}
                      <span className="text-amber-700">
                        {' '}— {r.rows} row{r.rows === 1 ? '' : 's'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* The rows this upload would not invent a company for. Beside
                "Filed Under You" because it is the same kind of fact about the
                same kind of column — a name in the sheet that matches nothing
                here — and the difference is only what was done about it: a
                recruiter name falls back to the uploader, a client name has
                nowhere to fall back to. */}
            {(result.unresolvedClients ?? []).length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <h4 className="text-sm font-medium text-amber-900">
                  Clients Not On File
                </h4>
                <p className="mt-0.5 text-xs text-amber-800">
                  These Client names match nothing here, and this upload was
                  told not to create companies — so their rows were skipped.
                  Add the client, or merge the spelling into an existing one on
                  the Clients page, then upload the same file again.
                </p>
                <ul className="mt-2 space-y-0.5">
                  {(result.unresolvedClients ?? []).map((c) => (
                    <li key={c.name} className="text-xs text-amber-900">
                      {c.name}
                      <span className="text-amber-700">
                        {' '}— {c.rows} row{c.rows === 1 ? '' : 's'} skipped
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* File-level, not per-sheet: these are complaints about cells,
                and which tab a cell is on is not what the reader needs. They
                went to the server log before this, one line per cell, where
                the person who uploaded the file could never see them. */}
            {(result.workbookWarnings ?? 0) > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <h4 className="text-sm font-medium text-gray-900">
                  What Excel Said About This File
                </h4>
                <p className="mt-0.5 text-xs text-gray-500">
                  {result.workbookWarnings} cell
                  {result.workbookWarnings === 1 ? '' : 's'} could not be read
                  as the type they are formatted as — most often a column
                  formatted as dates with something else typed into it. The
                  rows still imported.
                </p>
                <ul className="mt-1.5 space-y-0.5 text-[11px] text-gray-600">
                  {(result.workbookWarningSample ?? []).map((w) => (
                    <li key={w} className="truncate">{w}</li>
                  ))}
                </ul>
                {result.workbookWarnings
                  > (result.workbookWarningSample ?? []).length && (
                  <p className="mt-1 text-[11px] text-gray-500">
                    {result.workbookWarnings
                      - (result.workbookWarningSample ?? []).length}
                    {' '}more not listed.
                  </p>
                )}
              </div>
            )}

            {(result.conflicts ?? []).length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <h4 className="text-sm font-medium text-gray-900">
                  Kept What Was Already On Record
                </h4>
                <p className="mt-0.5 text-xs text-gray-500">
                  The sheet said something different. An upload never overwrites
                  an existing value — these are the disagreements, so you can
                  decide rather than discover.
                  {result.conflictsTruncated > 0
                    && ` ${result.conflictsTruncated} more not listed.`}
                </p>
                <div className="mt-2 max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-[11px] text-gray-500">
                      <tr>
                        <th className="py-1 font-medium">Candidate</th>
                        <th className="py-1 font-medium">Field</th>
                        <th className="py-1 font-medium">Kept</th>
                        <th className="py-1 font-medium">Sheet Said</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(result.conflicts ?? []).map((c, index) => (
                        <tr key={`${c.candidate}-${c.field}-${index}`}>
                          <td className="py-1 text-gray-900">{c.candidate}</td>
                          <td className="py-1 text-gray-500">{c.field}</td>
                          <td className="py-1 text-gray-900">{c.kept}</td>
                          <td className="py-1 text-amber-800">{c.fromSheet}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {!result && !upload.isPending && (
          <p className="flex items-start gap-1.5 text-xs text-gray-400">
            <FileSpreadsheet className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Known formats: Daily Calls Tracker, Daily Tracker and Placements.
            Anything else in the file is listed with the reason it was not read.
          </p>
        )}
      </div>
    </div>
  );
}
